import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Video, Crown, Trash2, ExternalLink, Search, Filter, 
  ChevronDown, ChevronUp, Medal, Gamepad2, Check, X, Clock,
  AlertCircle, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubmissionsTab = ({ 
  submissions, 
  challenges,
  winners, 
  token, 
  onPickWinner, 
  pickingWinner,
  onRefresh 
}) => {
  const [search, setSearch] = useState('');
  const [challengeFilter, setChallengeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [collapsedChallenges, setCollapsedChallenges] = useState({});
  const [initialCollapseSet, setInitialCollapseSet] = useState(false);

  // Set all challenges to collapsed on initial load
  useEffect(() => {
    const safeSubmissions = Array.isArray(submissions) ? submissions : [];
    if (safeSubmissions.length > 0 && !initialCollapseSet) {
      const allCollapsed = {};
      safeSubmissions.forEach(sub => {
        if (sub.challenge_id) {
          allCollapsed[sub.challenge_id] = true;
        }
      });
      setCollapsedChallenges(allCollapsed);
      setInitialCollapseSet(true);
    }
  }, [submissions, initialCollapseSet]);

  // Filter and sort submissions
  const filteredSubmissions = useMemo(() => {
    const safeSubmissions = Array.isArray(submissions) ? submissions : [];
    return safeSubmissions.filter(s => {
      const matchesSearch = search === '' || 
        s.twitch_username?.toLowerCase().includes(search.toLowerCase()) ||
        s.display_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.video_title?.toLowerCase().includes(search.toLowerCase());
      
      const matchesChallenge = challengeFilter === 'all' || s.challenge_id === challengeFilter;
      
      const status = s.status || 'pending';
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      
      return matchesSearch && matchesChallenge && matchesStatus;
    }).sort((a, b) => {
      if (sortOrder === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return new Date(a.created_at) - new Date(b.created_at);
    });
  }, [submissions, search, challengeFilter, statusFilter, sortOrder]);

  // Group submissions by challenge
  const groupedSubmissions = useMemo(() => {
    const groups = {};
    const safeChallenges = Array.isArray(challenges) ? challenges : [];
    filteredSubmissions.forEach(sub => {
      const challengeKey = sub.challenge_id || 'uncategorized';
      const challengeTitle = sub.challenge_title || 'General Submissions';
      if (!groups[challengeKey]) {
        const challenge = safeChallenges.find(c => c.id === challengeKey);
        groups[challengeKey] = {
          id: challengeKey,
          title: challengeTitle,
          game_name: sub.game_name || challenge?.game_name,
          status: challenge?.status || 'unknown',
          submissions: []
        };
      }
      groups[challengeKey].submissions.push(sub);
    });
    return groups;
  }, [filteredSubmissions, challenges]);

  // Count by status
  const statusCounts = useMemo(() => {
    const safeSubmissions = Array.isArray(submissions) ? submissions : [];
    return safeSubmissions.reduce((acc, s) => {
      const status = s.status || 'pending';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [submissions]);

  const handleApprove = async (submissionId) => {
    try {
      await axios.post(`${API}/admin/submissions/${submissionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission approved!');
      onRefresh();
    } catch (error) {
      toast.error('Failed to approve submission');
    }
  };

  const handleReject = async (submissionId) => {
    try {
      await axios.post(`${API}/admin/submissions/${submissionId}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission rejected');
      setRejectingId(null);
      setRejectReason('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to reject submission');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await axios.delete(`${API}/admin/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const isWinner = (submissionId) => winners.some(w => w.submission_id === submissionId);

  const getChallenge = (challengeId) => challenges.find(c => c.id === challengeId);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <Check className="w-3 h-3" />,
      rejected: <X className="w-3 h-3" />
    };
    return { style: styles[status] || styles.pending, icon: icons[status] || icons.pending };
  };

  return (
    <div className="space-y-4">
      {/* Status Summary */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <Clock className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 font-medium">{statusCounts.pending || 0}</span>
          <span className="text-yellow-400/70 text-sm">Pending</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-green-400 font-medium">{statusCounts.approved || 0}</span>
          <span className="text-green-400/70 text-sm">Approved</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
          <X className="w-4 h-4 text-red-400" />
          <span className="text-red-400 font-medium">{statusCounts.rejected || 0}</span>
          <span className="text-red-400/70 text-sm">Rejected</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by username or title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px] bg-zinc-900 border-zinc-800">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={challengeFilter} onValueChange={setChallengeFilter}>
          <SelectTrigger className="w-full sm:w-[200px] bg-zinc-900 border-zinc-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by challenge" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Challenges</SelectItem>
            {challenges.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-full sm:w-[150px] bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        Showing {filteredSubmissions.length} of {submissions.length} submissions in {Object.keys(groupedSubmissions).length} challenge{Object.keys(groupedSubmissions).length !== 1 ? 's' : ''}
      </p>

      {/* Grouped Submissions by Challenge */}
      <div className="space-y-6">
        {Object.entries(groupedSubmissions).map(([challengeId, group]) => {
          const isCollapsed = collapsedChallenges[challengeId];
          const challengeStatusColor = {
            active: 'border-green-500/30 bg-green-500/5',
            scheduled: 'border-yellow-500/30 bg-yellow-500/5',
            ended: 'border-gray-500/30 bg-gray-500/5'
          }[group.status] || 'border-blue-500/30 bg-blue-500/5';

          // Count statuses within this challenge
          const groupStatusCounts = group.submissions.reduce((acc, s) => {
            const status = s.status || 'pending';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});

          return (
            <div key={challengeId} className={`rounded-lg border ${challengeStatusColor}`}>
              {/* Challenge Header */}
              <button
                onClick={() => setCollapsedChallenges(prev => ({ ...prev, [challengeId]: !prev[challengeId] }))}
                className="w-full flex items-start sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-white/5 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    {group.title}
                  </h3>
                  <div className="flex items-center gap-2 sm:gap-3 text-sm flex-wrap">
                    {group.game_name && (
                      <span className="flex items-center gap-1 text-purple-400 text-xs sm:text-sm">
                        <Gamepad2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        {group.game_name}
                      </span>
                    )}
                    <span className="text-gray-500 text-xs sm:text-sm">{group.submissions.length} submission{group.submissions.length !== 1 ? 's' : ''}</span>
                    {/* Mini status counts - inline on mobile */}
                    {groupStatusCounts.pending > 0 && (
                      <span className="px-1.5 sm:px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] sm:text-xs rounded-full">
                        {groupStatusCounts.pending} pending
                      </span>
                    )}
                    {groupStatusCounts.approved > 0 && (
                      <span className="px-1.5 sm:px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] sm:text-xs rounded-full">
                        {groupStatusCounts.approved} approved
                      </span>
                    )}
                    {groupStatusCounts.rejected > 0 && (
                      <span className="px-1.5 sm:px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] sm:text-xs rounded-full">
                        {groupStatusCounts.rejected} rejected
                      </span>
                    )}
                  </div>
                </div>

                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${isCollapsed ? '' : 'rotate-180'}`} />
              </button>

              {/* Submissions Grid */}
              {!isCollapsed && (
                <div className="p-4 pt-0">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {group.submissions.map((submission) => {
                      const challenge = getChallenge(submission.challenge_id);
                      const alreadyWinner = isWinner(submission.id);
                      const challengeWinners = winners.filter(w => w.challenge_id === submission.challenge_id);
                      const maxReached = challenge && challengeWinners.length >= (challenge.max_winners || 1);
                      const nextPlace = challengeWinners.length + 1;
                      const status = submission.status || 'pending';
                      const statusBadge = getStatusBadge(status);
                      const isRejecting = rejectingId === submission.id;

                      return (
                        <Card 
                          key={submission.id} 
                          className={`glass-card overflow-hidden ${
                            alreadyWinner ? 'border-yellow-500/50' : 
                            status === 'pending' ? 'border-yellow-500/30' :
                            status === 'rejected' ? 'border-red-500/30 opacity-60' : ''
                          }`}
                        >
                          <CardContent className="p-4">
                            {/* Status Badge */}
                            <div className="flex items-center justify-between mb-3">
                              <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${statusBadge.style}`}>
                                {statusBadge.icon}
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </span>
                              {alreadyWinner && (
                                <Crown className="w-5 h-5 text-yellow-500" />
                              )}
                            </div>

                            {/* User Info */}
                            <div className="flex items-center gap-3 mb-3">
                              <img 
                                src={submission.profile_image_url || 'https://via.placeholder.com/40'} 
                                alt={submission.display_name}
                                className="w-10 h-10 rounded-full"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-white truncate">{submission.display_name}</p>
                                <p className="text-xs text-gray-500">@{submission.twitch_username}</p>
                              </div>
                            </div>

                            {/* Video Info */}
                            <div className="bg-zinc-900/50 rounded-lg p-3 mb-3">
                              <div className="flex items-start gap-2">
                                <Video className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white truncate">
                                    {submission.video_title || 'Untitled'}
                                  </p>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    submission.video_type === 'twitch' ? 'bg-purple-500/20 text-purple-400' : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {submission.video_type}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Rejection Reason */}
                            {status === 'rejected' && submission.rejection_reason && (
                              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-3">
                                <p className="text-xs text-red-400 flex items-start gap-1">
                                  <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  {submission.rejection_reason}
                                </p>
                              </div>
                            )}

                            {/* Timestamp */}
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-red-900/20">
                              <span className="text-xs text-gray-500 font-mono">
                                {new Date(submission.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(submission.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                              </span>
                            </div>

                            {/* Rejection Input */}
                            {isRejecting && (
                              <div className="mt-3 space-y-2">
                                <Input
                                  placeholder="Reason for rejection (optional)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  className="bg-zinc-900 border-zinc-800 text-sm"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="flex-1"
                                    onClick={() => handleReject(submission.id)}
                                  >
                                    Confirm Reject
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            {!isRejecting && (
                              <div className="flex gap-2 mt-3 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                  onClick={() => window.open(submission.video_url, '_blank')}
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Watch
                                </Button>

                                {/* Approve/Reject buttons for pending submissions */}
                                {status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-xs"
                                      onClick={() => handleApprove(submission.id)}
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="text-xs"
                                      onClick={() => setRejectingId(submission.id)}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                
                                {/* Pick winner only for approved submissions */}
                                {status === 'approved' && !alreadyWinner && !maxReached && (
                                  <div className="flex items-center gap-1">
                                    {/* Show buttons for available places */}
                                    {challenge && challenge.max_winners >= 1 && !challengeWinners.some(w => w.place === 1) && (
                                      <Button
                                        size="sm"
                                        className="bg-yellow-600 hover:bg-yellow-700 text-xs px-2"
                                        onClick={() => onPickWinner(submission.id, 1)}
                                        disabled={pickingWinner === submission.id}
                                        title="Pick as 1st place"
                                      >
                                        <Medal className="w-3 h-3 mr-1" />
                                        {pickingWinner === submission.id ? '...' : '🥇'}
                                      </Button>
                                    )}
                                    {challenge && challenge.max_winners >= 2 && !challengeWinners.some(w => w.place === 2) && (
                                      <Button
                                        size="sm"
                                        className="bg-gray-500 hover:bg-gray-600 text-xs px-2"
                                        onClick={() => onPickWinner(submission.id, 2)}
                                        disabled={pickingWinner === submission.id}
                                        title="Pick as 2nd place"
                                      >
                                        <Medal className="w-3 h-3 mr-1" />
                                        {pickingWinner === submission.id ? '...' : '🥈'}
                                      </Button>
                                    )}
                                    {challenge && challenge.max_winners >= 3 && !challengeWinners.some(w => w.place === 3) && (
                                      <Button
                                        size="sm"
                                        className="bg-amber-600 hover:bg-amber-700 text-xs px-2"
                                        onClick={() => onPickWinner(submission.id, 3)}
                                        disabled={pickingWinner === submission.id}
                                        title="Pick as 3rd place"
                                      >
                                        <Medal className="w-3 h-3 mr-1" />
                                        {pickingWinner === submission.id ? '...' : '🥉'}
                                      </Button>
                                    )}
                                    {/* Fallback for challenges without max_winners set */}
                                    {(!challenge || !challenge.max_winners) && (
                                      <Button
                                        size="sm"
                                        className="bg-yellow-600 hover:bg-yellow-700 text-xs"
                                        onClick={() => onPickWinner(submission.id, nextPlace)}
                                        disabled={pickingWinner === submission.id}
                                      >
                                        <Medal className="w-3 h-3 mr-1" />
                                        {pickingWinner === submission.id ? '...' : `${nextPlace}${nextPlace === 1 ? 'st' : nextPlace === 2 ? 'nd' : 'rd'}`}
                                      </Button>
                                    )}
                                  </div>
                                )}
                                
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => handleDeleteSubmission(submission.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredSubmissions.length === 0 && (
        <Card className="glass-card p-8 text-center">
          <Video className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No submissions found</p>
        </Card>
      )}
    </div>
  );
};

export default SubmissionsTab;
